import { EmptyState, FlexColumn, FlexRow, Loading, MaterialIcon, useLoading } from '@gorila-shared-ui/components';
import { Button } from 'baseui/button';
import { HeadingSmall } from 'baseui/typography';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import AutoSizer from 'react-virtualized-auto-sizer';
import { useRecoilState, useRecoilValue } from 'recoil';
import { useFeedback } from '../../hooks/useFeedback';
import { useStyles } from '../../hooks/useStyles';
import { getMultipleDevices } from '../../services/deviceServices';
import { apiParamsState } from '../../storage/app';
import { assetState } from '../../storage/assets';
import { multipleDeviceState } from '../../storage/devices';
import { getPastelColor } from '../../utils/map';
import MultipleDeviceCard from '../devices/MultipleDeviceCard';
import { MultipleDevicesInfo } from '../devices/MultipleDevicesInfo';
import { EvaluationCard } from '../evaluations/EvaluationCard';
import { FloatingCardsLayout } from '../layout/FloatingCardsLayout';
import { FloatingCardContent } from '../layout/cards/FloatingCardContent';
import { StyledTooltip } from '../ui/StyledTooltip';

export function SecondariesPage() {
  const { theme, css } = useStyles();
  const apiParams = useRecoilValue(apiParamsState);
  const { assetId } = useParams();
  const asset = useRecoilValue(assetState(assetId ?? ''));
  const [multipleDevice, setMultipleDevice] = useRecoilState(multipleDeviceState(assetId ?? ''));
  const { loading, startLoading, stopLoading } = useLoading();
  const { showFailFeedback } = useFeedback();
  const [showEvaluations, setShowEvaluations] = useState(false);

  useEffect(() => {
    const loadMultipleDevices = async () => {
      if (!apiParams?.clientId || !asset?._id || !asset.subClient?._id) return;
      startLoading();
      const { multipleDevice, error } = await getMultipleDevices(apiParams.clientId, asset.subClient._id, asset._id);
      if (!error && multipleDevice) {
        setMultipleDevice({
          devices: multipleDevice.devices
            .map((device) => ({ ...device, color: getPastelColor() }))
            .sort((a, b) => +b.isPrimary - +a.isPrimary),
          evaluations: multipleDevice.evaluations,
        });
      } else {
        showFailFeedback(error || 'Ocurrió un error al cargar los dispositivos secundarios.');
      }
      stopLoading();
    };

    loadMultipleDevices();
  }, [apiParams?.clientId, asset?._id, asset?.subClient?._id]);

  const hasEvaluations = !!multipleDevice?.evaluations.length;

  return (
    <FloatingCardsLayout
      contentSize={470}
      listSize={660}
      content={
        hasEvaluations && showEvaluations ? (
          <FloatingCardContent
            flexible
            gap={theme.sizing.scale600}
          >
            <FlexRow
              classNames={css({
                alignItems: 'center',
                justifyContent: 'space-between',
              })}
            >
              <HeadingSmall margin={0}>Evaluaciones</HeadingSmall>
              <StyledTooltip
                content="Cerrar"
                showArrow={false}
              >
                <Button
                  shape="circle"
                  kind="tertiary"
                  size="compact"
                  onClick={() => {
                    setShowEvaluations(false);
                  }}
                >
                  <MaterialIcon
                    name="close"
                    size="mini"
                  />
                </Button>
              </StyledTooltip>
            </FlexRow>
            <div style={{ flex: 1 }}>
              <AutoSizer>
                {({ width, height }) => (
                  <FlexColumn
                    classNames={css({
                      width: `${width}px`,
                      height: `${height}px`,
                      overflowY: 'auto',
                    })}
                  >
                    {multipleDevice?.evaluations && (
                      <FlexColumn
                        classNames={css({ flex: multipleDevice.evaluations.length ? 1 : 0, overflowY: 'auto' })}
                      >
                        {!multipleDevice.evaluations.length && <EmptyState title="No existen evaluaciones" />}
                        {multipleDevice.evaluations.map((evaluation, evaluationIndex) => (
                          <EvaluationCard
                            evaluation={evaluation}
                            key={evaluationIndex}
                          />
                        ))}
                      </FlexColumn>
                    )}
                  </FlexColumn>
                )}
              </AutoSizer>
            </div>
          </FloatingCardContent>
        ) : undefined
      }
      list={
        <FloatingCardContent
          flexible
          gap={theme.sizing.scale600}
        >
          {loading && <Loading />}
          {!loading && asset && (
            <>
              <MultipleDevicesInfo
                onShowEvaluations={
                  hasEvaluations
                    ? () => {
                        setShowEvaluations(true);
                      }
                    : undefined
                }
              />
              <div style={{ flex: 1 }}>
                <AutoSizer>
                  {({ width, height }) => (
                    <FlexColumn
                      classNames={css({
                        width: `${width}px`,
                        height: `${height}px`,
                        overflowY: 'auto',
                      })}
                    >
                      {multipleDevice?.devices?.map((secondaryDevice) => (
                        <MultipleDeviceCard
                          key={secondaryDevice.ident}
                          device={secondaryDevice}
                          asset={asset}
                        />
                      ))}
                    </FlexColumn>
                  )}
                </AutoSizer>
              </div>
            </>
          )}
        </FloatingCardContent>
      }
    />
  );
}
